import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext';
import { adminAPI } from '../../utils/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalConsultations: 0,
    totalRevenue: 0,
    pendingApprovals: 0,
    pendingWithdrawals: 0
  });
  const [users, setUsers] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tab state: 'doctors', 'retailers', 'clinics', 'hospitals', 'medicines'
  const [activeTab, setActiveTab] = useState('doctors');

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, usersRes, medicinesRes] = await Promise.all([
        adminAPI.getDashboardStats().catch(() => ({ data: { success: false } })),
        adminAPI.getPendingUsers().catch(() => ({ data: { success: false } })),
        adminAPI.getPendingMedicines().catch(() => ({ data: { success: false } }))
      ]);

      if (statsRes.data?.success) {
        setStats(statsRes.data.data || stats);
      }

      if (usersRes.data?.success) {
        setUsers(usersRes.data.data || []);
      }

      if (medicinesRes.data?.success) {
        setMedicines(medicinesRes.data.data || []);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId) => {
    try {
      const res = await adminAPI.approveUser(userId);
      if (res.data?.success) {
        showSuccess('User approved successfully');
        fetchDashboardData();
      } else {
        showError(res.data?.message || 'Failed to approve user');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to approve user');
    }
  };

  const rejectUser = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user?')) return;
    try {
      const res = await adminAPI.rejectUser(userId);
      if (res.data?.success) {
        showSuccess('User rejected successfully');
        fetchDashboardData();
      } else {
        showError(res.data?.message || 'Failed to reject user');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to reject user');
    }
  };

  const approveMedicine = async (medicineId) => {
    try {
      const res = await adminAPI.approveMedicine(medicineId);
      if (res.data?.success) {
        showSuccess('Medicine approved successfully');
        fetchDashboardData();
      } else {
        showError(res.data?.message || 'Failed to approve medicine');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to approve medicine');
    }
  };

  const getFilteredUsers = (role) => {
    return users.filter(user => user.role === role && !user.is_approved);
  };

  if (loading) {
    return <div className="text-center py-8">Loading dashboard...</div>;
  }

  const renderApprovalContent = () => {
    if (activeTab === 'medicines') {
      return (
        <div className="space-y-4">
          {medicines.filter(m => !m.is_approved).length === 0 ? (
            <div className="text-center py-8 text-gray-400">No pending medicine approvals</div>
          ) : (
            medicines.filter(m => !m.is_approved).map((medicine) => (
              <div key={medicine._id} className="flex justify-between items-center p-4 bg-gray-700 rounded-lg">
                <div>
                  <h3 className="font-medium">{medicine.name}</h3>
                  <p className="text-sm text-gray-400">Stock: {medicine.stock_quantity}</p>
                  <p className="text-sm text-gray-400 text-gold">Price: ₹{medicine.price}</p>
                </div>
                <button
                  onClick={() => approveMedicine(medicine._id)}
                  className="bg-green-600 text-white px-3 py-1 rounded text-sm font-medium hover:bg-green-700"
                >
                  Approve
                </button>
              </div>
            ))
          )}
        </div>
      );
    }

    // Role based filtering
    const roleMap = {
      'doctors': 'doctor',
      'retailers': 'retailer',
      'clinics': 'clinic',
      'hospitals': 'hospital'
    };
    const targetRole = roleMap[activeTab];
    const filteredUsers = getFilteredUsers(targetRole);

    return (
      <div className="space-y-4">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            No pending {targetRole} approvals
          </div>
        ) : (
          filteredUsers.map((user) => (
            <div key={user._id} className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 bg-gray-700 rounded-lg gap-4">
              <div>
                <h3 className="font-medium text-lg">{user.name}</h3>
                <p className="text-sm text-gray-400">{user.email}</p>
                <p className="text-sm text-gray-400">Phone: {user.phone || 'N/A'}</p>
                <p className="text-sm text-gray-400">Joined: {new Date(user.createdAt).toLocaleDateString()}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => rejectUser(user._id)}
                  className="bg-red-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-red-700 transition"
                >
                  Reject
                </button>
                <button
                  onClick={() => approveUser(user._id)}
                  className="bg-green-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-green-700 transition"
                >
                  Approve
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gold">Admin Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Total Users</h3>
          <p className="text-2xl font-bold text-gold">{stats.totalUsers}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Consultations</h3>
          <p className="text-2xl font-bold text-gold">{stats.totalConsultations}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Total Revenue</h3>
          <p className="text-2xl font-bold text-gold">₹{stats.totalRevenue}</p>
        </div>
        <div
          onClick={() => navigate('/admin/users/pending')}
          className="bg-gray-800 rounded-xl p-6 cursor-pointer hover:border-gold transition-colors"
        >
          <h3 className="text-gray-400 text-sm mb-2">Pending Approvals</h3>
          <p className="text-2xl font-bold text-gold">{stats.pendingApprovals}</p>
          <p className="text-xs text-gray-500 mt-2">Click to verify users</p>
        </div>
      </div>

      {/* Pending Approvals Section with Tabs */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4 text-white">Pending Approvals</h2>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-700 pb-2">
          {['doctors', 'retailers', 'clinics', 'hospitals', 'medicines'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${activeTab === tab
                ? 'bg-gray-700 text-gold border-b-2 border-gold'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        {renderApprovalContent()}
      </div>

      {/* Actions / Quick Links */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4 text-gold">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/admin/content/sliders')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center"
          >
            <span className="block font-medium">Manage Sliders</span>
          </button>
          <button
            onClick={() => navigate('/admin/content/yoga')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center"
          >
            <span className="block font-medium">Manage Yoga Videos</span>
          </button>
          <button
            onClick={() => navigate('/admin/content/podcasts')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center"
          >
            <span className="block font-medium">Manage Podcasts</span>
          </button>
          <button
            onClick={() => navigate('/admin/content/food-links')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center"
          >
            <span className="block font-medium">Manage Food Links</span>
          </button>
          <button
            onClick={() => navigate('/admin/subscriptions/plans')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center"
          >
            <span className="block font-medium">Subscription Plans</span>
          </button>
          <button
            onClick={() => navigate('/admin/withdrawals')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center"
          >
            <span className="block font-medium">
              Withdrawals
              {stats.pendingWithdrawals > 0 && (
                <span className="ml-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {stats.pendingWithdrawals}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => navigate('/admin/users/all')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center border border-transparent hover:border-gold"
          >
            <span className="block font-medium">Manage All Users</span>
          </button>
          <button
            onClick={() => navigate('/admin/rules')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center border border-transparent hover:border-gold"
          >
            <span className="block font-medium">Rules & Compliance</span>
          </button>
          <button
            onClick={() => navigate('/admin/support')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center border border-transparent hover:border-gold"
          >
            <span className="block font-medium">Support Tickets</span>
          </button>
          <button
            onClick={() => navigate('/admin/blue-tick')}
            className="p-4 bg-blue-900/30 rounded-lg hover:bg-blue-800/40 transition text-center border border-blue-500/30 hover:border-blue-400"
          >
            <span className="block font-medium text-blue-400 flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293l-4 4a1 1 0 01-1.414 0l-2-2a1 1 0 111.414-1.414L9 10.586l3.293-3.293a1 1 0 111.414 1.414z" /></svg>
              Blue Tick Management
            </span>
          </button>
          <button
            onClick={() => navigate('/admin/feature-controls')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center border border-transparent hover:border-gold"
          >
            <span className="block font-medium">Feature Controls</span>
          </button>
          <button
            onClick={() => navigate('/admin/consultation-settings')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center border border-transparent hover:border-gold"
          >
            <span className="block font-medium">Consultation Settings</span>
          </button>
          <button
            onClick={() => navigate('/admin/users/manage')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center border border-transparent hover:border-gold"
          >
            <span className="block font-medium">User Management</span>
          </button>
          <button
            onClick={() => navigate('/admin/reels')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center border border-transparent hover:border-gold"
          >
            <span className="block font-medium">Reels Management</span>
          </button>
          <button
            onClick={() => navigate('/admin/reel-subscription-settings')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center border border-transparent hover:border-gold"
          >
            <span className="block font-medium">Reel Subscription Pricing</span>
          </button>
          <button
            onClick={() => navigate('/admin/audit-logs')}
            className="p-4 bg-amber-900/30 rounded-lg hover:bg-amber-800/40 transition text-center border border-amber-500/30 hover:border-amber-400"
          >
            <span className="block font-medium text-amber-400">Audit Logs</span>
          </button>
          <button
            onClick={() => navigate('/admin/appointments/analytics')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center border border-transparent hover:border-gold"
          >
            <span className="block font-medium">Appointment Analytics</span>
          </button>
          <button
            onClick={() => navigate('/admin/partners')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition text-center border border-transparent hover:border-gold"
          >
            <span className="block font-medium">Partner Management</span>
          </button>
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
